import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { decode } from 'html-entities'

import Dropdown from '../../components/common/TheSelect'
import Button from '../../components/common/TheButton'
import Alert from '../../components/common/TheAlert'
import Loader from '../../components/common/TheLoader'

import { selectUser } from '../auth/authSlice'
import {
  useAddServiceRequestMutation,
  useGetServReqReqClQuery,
  useGetServReqServIdQuery,
} from './serviceRequestSlice'

// 요청유형 (PC ProcessConstant)
//   0223: 장애처리, 0215: 단순문의, 0179: 변경개발
const REQ_TY_SE_INCIDENT = '0223'

// 고정값 (모바일은 항상 내부요청등록, 업무, 모바일 경로 → 임시 0063)
const FIXED = {
  regTySe: '0231', // SR_REG_SE_4 = 내부요청등록
  reqPathSe: '0063', // 모바일 경로 코드 미정 — 임시로 웹요청 사용
  confRscAt: '1', // 1=업무(요청서비스) / 2=구성자원(미사용)
  tmlMgmtTy: 'CM100601', // 템플릿 유형 = 텍스트 형 (모바일은 평문 입력만 지원)
}

// 완료희망일시 기본 offset (오늘 + N일). 사용자가 datetime-local에서 자유롭게 수정 가능.
const DEFAULT_CMPL_HOPE_DAYS = 3

const reqTypeOptions = [
  { label: '요청유형 선택', value: '' },
  { label: '장애처리', value: REQ_TY_SE_INCIDENT },
  { label: '단순문의', value: '0215' },
  { label: '변경개발', value: '0179' },
]

// Date → 'yyyy-MM-ddTHH:mm' (datetime-local input value)
const toLocalInputValue = date => {
  const pad = n => String(n).padStart(2, '0')
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  )
}

const getDefaultCmplHopeDt = () => {
  const d = new Date()
  d.setDate(d.getDate() + DEFAULT_CMPL_HOPE_DAYS)
  return toLocalInputValue(d)
}

const buildInitialForm = () => ({
  reqTySe: '',
  reqCl: '',
  servId: '',
  srTitlNm: '',
  srCn: '',
  chgCmplHopeDt: getDefaultCmplHopeDt(),
  icdtOcrDt: '',
})

// 'yyyy-MM-ddTHH:mm' → 'yyyyMMddHHmm'
const toServerDateTime = v => (v ? v.replace(/[-T:]/g, '') : '')

export default function ServiceRequest() {
  const navigate = useNavigate()
  const user = useSelector(selectUser)
  const coId = user?.domain ?? ''
  const userId = user?.id ?? ''
  const userNm = user?.name ?? ''

  const [form, setForm] = useState(buildInitialForm)
  const [errors, setErrors] = useState({})
  const [files, setFiles] = useState([])

  const isIncident = form.reqTySe === REQ_TY_SE_INCIDENT

  // 요청분류 목록
  const {
    data: reqClList = [],
    isFetching: isReqClFetching,
  } = useGetServReqReqClQuery(
    { reqTySe: form.reqTySe },
    { skip: !form.reqTySe }
  )

  const reqClOptions = useMemo(() => {
    const opts = (reqClList ?? []).map(item => ({
      label: decode(item.reqClNm ?? ''),
      value: item.reqCl,
    }))
    return [{ label: '요청분류 선택', value: '' }, ...opts]
  }, [reqClList])

  // 요청서비스 목록
  const {
    data: servList = [],
    isFetching: isServFetching,
  } = useGetServReqServIdQuery(
    { coId, reqCl: form.reqCl },
    { skip: !coId || !form.reqCl }
  )

  const servOptions = useMemo(() => {
    const opts = (servList ?? []).map(item => ({
      label: decode(item.servPathNm || item.servNm || ''),
      value: item.servId,
    }))
    return [{ label: '요청서비스 선택', value: '' }, ...opts]
  }, [servList])

  const updateField = (key, value) => {
    setForm(prev => {
      const next = { ...prev, [key]: value }
      // 상위 변경 시 하위 필드 리셋
      if (key === 'reqTySe') {
        next.reqCl = ''
        next.servId = ''
        // 장애처리로 전환되면 발생일시를 현재시각으로 기본 세팅
        next.icdtOcrDt =
          value === REQ_TY_SE_INCIDENT ? toLocalInputValue(new Date()) : ''
      } else if (key === 'reqCl') {
        next.servId = ''
      }
      return next
    })
    if (errors[key]) {
      setErrors(prev => ({ ...prev, [key]: false }))
    }
  }

  const onSelectFiles = e => {
    const selected = Array.from(e.target.files || [])
    if (!selected.length) return
    setFiles(prev => [...prev, ...selected])
    e.target.value = ''
  }

  const removeFile = idx => {
    setFiles(prev => prev.filter((_, i) => i !== idx))
  }

  const validate = () => {
    const next = {
      reqTySe: !form.reqTySe,
      reqCl: !form.reqCl,
      servId: !form.servId,
      srTitlNm: !form.srTitlNm.trim(),
      srCn: !form.srCn.trim(),
      chgCmplHopeDt: !form.chgCmplHopeDt,
      icdtOcrDt: isIncident && !form.icdtOcrDt,
    }
    setErrors(next)
    return !Object.values(next).some(Boolean)
  }

  const [addServiceRequest, { isLoading: isSubmitting }] =
    useAddServiceRequestMutation()

  const [isShowConfirm, setIsShowConfirm] = useState(false)
  const [isShowComplete, setIsShowComplete] = useState(false)
  const [isShowError, setIsShowError] = useState(false)
  const [alertMsg, setAlertMsg] = useState('')

  const closeConfirm = useCallback(() => setIsShowConfirm(false), [])
  const closeError = useCallback(() => setIsShowError(false), [])

  const onClickSubmit = e => {
    e.preventDefault()
    if (!validate()) {
      setAlertMsg('필수 항목을 입력해주세요.')
      setIsShowError(true)
      return
    }
    setAlertMsg('서비스 요청을 등록하시겠습니까?')
    setIsShowConfirm(true)
  }

  const buildFormData = () => {
    const fd = new FormData()
    // 요청정보
    fd.append('reqTySe', form.reqTySe)
    fd.append('reqCl', form.reqCl)
    fd.append('servId', form.servId)
    fd.append('srTitlNm', form.srTitlNm)
    fd.append('srCn', form.srCn)
    fd.append('chgCmplHopeDt', toServerDateTime(form.chgCmplHopeDt))
    if (isIncident) {
      fd.append('icdtOcrDt', toServerDateTime(form.icdtOcrDt))
    }

    // 로그인 기반 고정값
    fd.append('srCcompId', coId)
    fd.append('qusrCompId', coId)
    fd.append('qusrId', userId)
    fd.append('qusrNm', userNm)
    fd.append('regTySe', FIXED.regTySe)
    fd.append('reqPathSe', FIXED.reqPathSe)
    fd.append('confRscAt', FIXED.confRscAt)
    fd.append('tmlMgmtTy', FIXED.tmlMgmtTy)

    // 첨부파일
    files.forEach((file, index) => {
      fd.append(`files[${index}]`, file)
    })

    return fd
  }

  const onConfirmSave = async () => {
    setIsShowConfirm(false)
    try {
      await addServiceRequest(buildFormData()).unwrap()
      setAlertMsg('서비스 요청이 완료되었습니다.\n목록으로 이동합니다.')
      setIsShowComplete(true)
    } catch (err) {
      setAlertMsg('등록 중 오류가 발생했습니다. 관리자에게 문의해주세요.')
      setIsShowError(true)
    }
  }

  const onCompleteClose = () => {
    setIsShowComplete(false)
    navigate('/search-request')
  }

  const onClickReset = () => {
    setForm(buildInitialForm())
    setErrors({})
    setFiles([])
  }

  const isLoading = isReqClFetching || isServFetching

  return (
    <main className='content pt-60'>
      <div className='content__blocks p-0'>
        <div
          className='blue__card bg-blue-100'
          style={{ padding: '32px 16px' }}>
          {isLoading && <Loader className='loading' />}

          <Dropdown
            id='select_req_ty_se'
            label='요청유형'
            options={reqTypeOptions}
            onChange={e => updateField('reqTySe', e.curr.value)}
          />
          {errors.reqTySe && (
            <div
              className='error-text'
              style={{ marginTop: '-12px', marginBottom: '12px' }}>
              요청유형을 선택해주세요.
            </div>
          )}

          <Dropdown
            id='select_req_cl'
            label='요청분류'
            options={reqClOptions}
            disabled={!form.reqTySe}
            onChange={e => updateField('reqCl', e.curr.value)}
          />
          {errors.reqCl && (
            <div
              className='error-text'
              style={{ marginTop: '-12px', marginBottom: '12px' }}>
              요청분류를 선택해주세요.
            </div>
          )}

          <Dropdown
            id='select_serv'
            label='요청서비스'
            options={servOptions}
            disabled={!form.reqCl}
            onChange={e => updateField('servId', e.curr.value)}
          />
          {errors.servId && (
            <div
              className='error-text'
              style={{ marginTop: '-12px', marginBottom: '12px' }}>
              요청서비스를 선택해주세요.
            </div>
          )}

          <span
            className='text-primary fw-600'
            style={{ display: 'block' }}>
            제목
          </span>
          <input
            type='text'
            className={`text__input${errors.srTitlNm ? ' is-error' : ''}`}
            placeholder='제목을 입력하세요'
            value={form.srTitlNm}
            maxLength={100}
            style={{ marginTop: '6px', marginBottom: '4px' }}
            onChange={e => updateField('srTitlNm', e.target.value)}
          />
          {errors.srTitlNm && (
            <div
              className='error-text'
              style={{ marginBottom: '12px' }}>
              제목을 입력해주세요.
            </div>
          )}

          <span
            className='text-primary fw-600'
            style={{ display: 'block', marginTop: '12px' }}>
            내용
          </span>
          <textarea
            className='text__textarea'
            placeholder='요청 내용을 입력하세요'
            value={form.srCn}
            maxLength={4000}
            style={{ marginTop: '6px', marginBottom: '4px' }}
            onChange={e => updateField('srCn', e.target.value)}
          />
          {errors.srCn && (
            <div
              className='error-text'
              style={{ marginBottom: '12px' }}>
              내용을 입력해주세요.
            </div>
          )}

          {isIncident && (
            <>
              <span
                className='text-primary fw-600'
                style={{ display: 'block' }}>
                장애인지일시
              </span>
              <input
                type='datetime-local'
                className='text__input'
                style={{ marginTop: '6px', marginBottom: '4px' }}
                value={form.icdtOcrDt}
                onChange={e => updateField('icdtOcrDt', e.target.value)}
              />
              {errors.icdtOcrDt && (
                <div
                  className='error-text'
                  style={{ marginBottom: '12px' }}>
                  장애인지일시를 선택해주세요.
                </div>
              )}
            </>
          )}

          <span
            className='text-primary fw-600'
            style={{ display: 'block', marginTop: isIncident ? '12px' : '0' }}>
            완료희망일시
          </span>
          <input
            type='datetime-local'
            className='text__input'
            style={{ marginTop: '6px', marginBottom: '4px' }}
            value={form.chgCmplHopeDt}
            onChange={e => updateField('chgCmplHopeDt', e.target.value)}
          />
          {errors.chgCmplHopeDt && (
            <div
              className='error-text'
              style={{ marginBottom: '12px' }}>
              완료희망일시를 선택해주세요.
            </div>
          )}

          <span
            className='text-primary fw-600'
            style={{ display: 'block', marginTop: '12px' }}>
            첨부파일
          </span>
          <div style={{ marginTop: '6px', marginBottom: '12px' }}>
            <label
              className='button is-default is-secondary'
              style={{
                display: 'inline-block',
                padding: '8px 16px',
                cursor: 'pointer',
              }}>
              파일 선택
              <input
                type='file'
                multiple
                style={{ display: 'none' }}
                onChange={onSelectFiles}
              />
            </label>
            {files.length > 0 && (
              <ul
                style={{
                  listStyle: 'none',
                  padding: 0,
                  marginTop: '8px',
                }}>
                {files.map((file, idx) => (
                  <li
                    key={`${file.name}-${idx}`}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '4px 0',
                      fontSize: '14px',
                    }}>
                    <span
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1,
                        marginRight: '8px',
                      }}>
                      {file.name}
                    </span>
                    <button
                      type='button'
                      onClick={() => removeFile(idx)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#bd0000',
                        cursor: 'pointer',
                        fontSize: '14px',
                      }}>
                      삭제
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <p
              style={{
                fontSize: '12px',
                color: '#666',
                marginTop: '4px',
              }}>
              최대 10MB까지 첨부할 수 있습니다.
            </p>
          </div>

          {isSubmitting && <Loader className='loading' />}

          <div className='d-flex'>
            <Button
              type='btn'
              variant='secondary'
              overrideStyle={{ flex: 1, marginRight: '4px' }}
              onClick={onClickReset}>
              초기화
            </Button>
            <Button
              type='btn'
              overrideStyle={{ flex: 1 }}
              disabled={isSubmitting}
              onClick={onClickSubmit}>
              등록
            </Button>
          </div>
        </div>
      </div>

      <Alert
        isShow={isShowConfirm}
        type='confirm'
        content={alertMsg}
        onClose={closeConfirm}
        onConfirm={onConfirmSave}
      />
      <Alert
        isShow={isShowComplete}
        type='confirm'
        hideClose
        content={alertMsg}
        onClose={onCompleteClose}
        onConfirm={onCompleteClose}
      />
      <Alert
        isShow={isShowError}
        type='error'
        hideClose
        content={alertMsg}
        onClose={closeError}
        onConfirm={closeError}
      />
    </main>
  )
}